import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ArticleGrid from '../components/home/ArticleGrid';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getCategoryBySlug, getArticlesByCategory } from '../services/api';

const CategoryPage = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [category, setCategory] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);

  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  useEffect(() => {
    const fetchCategoryData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Récupération de la catégorie pour affichage (optionnel)
        const categoryData = await getCategoryBySlug(slug);
        if (!categoryData) {
          setError('Catégorie non trouvée');
          setLoading(false);
          return;
        }
        setCategory(categoryData);

     const data = await getArticlesByCategory(categoryData.slug, currentPage, 12);


        setArticles(data.articles || []);
        setTotalPages(data.totalPages || 1);
      } catch (err) {
        console.error('Erreur lors du chargement de la catégorie:', err);
        setError('Impossible de charger la catégorie');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchCategoryData();
    }
  }, [slug, currentPage]);

  useEffect(() => {
    if (category) {
      document.title = `${category.name} - Xibaar Bambouck`;
    }
    return () => {
      document.title = 'Xibaar Bambouck';
    };
  }, [category]);

  const handlePageChange = (page) => {
    setSearchParams({ page: page.toString() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-red-600 mb-4">Erreur</h2>
        <p className="text-gray-600">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{category?.name}</h1>
          {category?.description && (
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{category.description}</p>
          )}
          <div className="mt-4 text-sm text-gray-500">
            {articles.length} article{articles.length > 1 ? 's' : ''} trouvé{articles.length > 1 ? 's' : ''}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {articles.length > 0 ? (
          <>
            <ArticleGrid articles={articles} />

            {totalPages > 1 && (
              <div className="mt-12 flex justify-center">
                <nav className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 rounded ${
                      currentPage === 1
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border'
                    }`}
                  >
                    Précédent
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-4 py-2 rounded ${
                        page === currentPage
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border'
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-2 rounded ${
                      currentPage === totalPages
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border'
                    }`}
                  >
                    Suivant
                  </button>
                </nav>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="w-24 h-24 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Aucun article trouvé</h3>
            <p className="text-gray-600">Il n'y a actuellement aucun article dans cette catégorie.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
